"use client"

import type { BetterFetchError } from "@better-fetch/fetch"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@papier/ui/components/ui/button"
import { Checkbox } from "@papier/ui/components/ui/checkbox"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@papier/ui/components/ui/form"
import { Input } from "@papier/ui/components/ui/input"
import { InputOTP } from "@papier/ui/components/ui/input-otp"
import { Separator } from "@papier/ui/components/ui/separator"
import { cn } from "@papier/ui/lib/utils"
import { Check, Copy, Refresh as Loader2, QrCode as QrCodeIcon, Plain as SendIcon } from "@papier/ui/components/icons"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { QRCode } from "react-qr-code"
import * as z from "zod"
import { useIsHydrated } from "../../../hooks/use-hydrated"
import { useOnSuccessTransition } from "../../../hooks/use-success-transition"
import { AuthUIContext } from "../../../lib/auth-ui-provider"
import { getLocalizedError, getSearchParam } from "../../../lib/utils"
import type { AuthLocalization } from "../../../localization/auth-localization"
import type { User } from "../../../types/auth-client"
import type { AuthFormClassNames } from "../auth-form"
import { OTPInputGroup } from "../otp-input-group"
import { TwoFactorScanHelpDialog } from "../two-factor-scan-help-dialog"

export interface TwoFactorFormProps {
    className?: string
    classNames?: AuthFormClassNames
    isSubmitting?: boolean
    localization?: Partial<AuthLocalization>
    otpSeparators?: 0 | 1 | 2
    redirectTo?: string
    setIsSubmitting?: (value: boolean) => void
}

export function TwoFactorForm({
    className,
    classNames,
    isSubmitting,
    localization,
    otpSeparators = 0,
    redirectTo,
    setIsSubmitting
}: TwoFactorFormProps) {
    const isHydrated = useIsHydrated()
    const totpURI = isHydrated ? getSearchParam("totpURI") : null
    const initialSendRef = useRef(false)

    const {
        authClient,
        basePath,
        hooks: { useSession },
        localization: contextLocalization,
        twoFactor,
        viewPaths,
        toast,
        Link
    } = useContext(AuthUIContext)

    localization = { ...contextLocalization, ...localization }

    const { onSuccess, isPending: transitionPending } = useOnSuccessTransition({
        redirectTo
    })

    const { data: sessionData } = useSession()
    const isTwoFactorEnabled = (sessionData?.user as User)?.twoFactorEnabled

    const [method, setMethod] = useState<"totp" | "otp" | null>(
        twoFactor?.length === 1 ? twoFactor[0] : null
    )

    const [isSendingOtp, setIsSendingOtp] = useState(false)
    const [cooldownSeconds, setCooldownSeconds] = useState(0)
    const [copied, setCopied] = useState(false)

    const formSchema = z.object({
        code: z
            .string()
            .min(1, {
                message: `${localization.ONE_TIME_PASSWORD} ${localization.IS_REQUIRED}`
            })
            .min(6, {
                message: `${localization.ONE_TIME_PASSWORD} ${localization.IS_INVALID}`
            }),
        trustDevice: z.boolean().optional()
    })

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            code: ""
        }
    })

    isSubmitting =
        isSubmitting || form.formState.isSubmitting || transitionPending

    useEffect(() => {
        setIsSubmitting?.(form.formState.isSubmitting || transitionPending)
    }, [form.formState.isSubmitting, transitionPending, setIsSubmitting])

    useEffect(() => {
        if (
            method === "otp" &&
            cooldownSeconds <= 0 &&
            !initialSendRef.current
        ) {
            initialSendRef.current = true
            sendOtp()
        }
    }, [method])

    useEffect(() => {
        if (cooldownSeconds <= 0) return

        const timer = setTimeout(() => {
            setCooldownSeconds((prev) => prev - 1)
        }, 1000)
        return () => clearTimeout(timer)
    }, [cooldownSeconds])

    const sendOtp = async () => {
        if (isSendingOtp || cooldownSeconds > 0) return

        try {
            setIsSendingOtp(true)
            await authClient.twoFactor.sendOtp({
                fetchOptions: { throw: true }
            })
            setCooldownSeconds(60)
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })

            if (
                (error as BetterFetchError).error.code ===
                "INVALID_TWO_FACTOR_COOKIE"
            ) {
                history.back()
            }
        }

        initialSendRef.current = false
        setIsSendingOtp(false)
    }

    async function verifyCode({
        code,
        trustDevice
    }: z.infer<typeof formSchema>) {
        try {
            const verifyMethod =
                method === "totp"
                    ? authClient.twoFactor.verifyTotp
                    : authClient.twoFactor.verifyOtp

            await verifyMethod({
                code,
                trustDevice,
                fetchOptions: { throw: true }
            })

            await onSuccess()

            if (sessionData && !isTwoFactorEnabled) {
                toast({
                    variant: "success",
                    message: localization?.TWO_FACTOR_ENABLED
                })
            }
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })

            form.reset()
        }
    }

    const getSecretFromTotp = useCallback((): string | null => {
        if (!totpURI) return null
        const url = new URL(totpURI)
        const secret = url.searchParams.get("secret")
        if (!secret) return null
        return secret.match(/.{1,4}/g)?.join("-") ?? secret
    }, [totpURI])

    const handleCopy = useCallback(() => {
        const secret = getSecretFromTotp()
        if (!secret) return
        navigator.clipboard.writeText(secret.replace(/-/g, "")).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }, [getSecretFromTotp])

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(verifyCode)}
                className={cn("grid w-full gap-6", className, classNames?.base)}
            >
                {twoFactor?.includes("totp") &&
                    totpURI &&
                    method === "totp" && (
                        <div className="grid gap-3">
                            <QRCode
                                fgColor="#000000"
                                bgColor="#ffffff"
                                className={cn(
                                    "border-border mx-auto size-50 rounded-lg border bg-white p-3",
                                    classNames?.qrCode
                                )}
                                value={totpURI}
                            />
                             <div className="flex justify-center">
                                <TwoFactorScanHelpDialog />
                            </div>

                            <div className="flex w-full items-center gap-2">
                                <Separator className="flex-1" />
                                <span className="text-muted-foreground flex-shrink-0 text-sm">
                                    Configurează manual
                                </span>
                                <Separator className="flex-1" />
                            </div>

                            <div className="space-y-4">
                                <div className="relative">
                                    <Input
                                        readOnly
                                        value={getSecretFromTotp() ?? ""}
                                        className="bg-muted/50 border-border pr-12 font-mono text-sm"
                                    />
                                    <Button
                                        size="sm"
                                        type="button"
                                        variant="ghost"
                                        className="hover:bg-accent absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
                                        onClick={handleCopy}
                                    >
                                        {copied ? (
                                            <Check className="size-4" />
                                        ) : (
                                            <Copy className="text-muted-foreground size-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                           
                        </div>
                    )}

                {method !== null && (
                    <div className="grid gap-4">
                        <FormField
                            control={form.control}
                            name="code"
                            render={({ field }) => (
                                <FormItem className="gap-3">
                                    <div className="flex items-center justify-between">
                                        <FormLabel
                                            className={classNames?.label}
                                        >
                                            {localization.ONE_TIME_PASSWORD}
                                        </FormLabel>
                                    </div>

                                    <FormControl>
                                        <InputOTP
                                            {...field}
                                            maxLength={6}
                                            onChange={(value) => {
                                                field.onChange(value)
                                            }}
                                            containerClassName={cn(
                                                "flex w-full justify-center",
                                                classNames?.otpInputContainer
                                            )}
                                            className={cn(
                                                "w-full",
                                                classNames?.otpInput
                                            )}
                                            disabled={isSubmitting}
                                        >
                                            <OTPInputGroup
                                                otpSeparators={otpSeparators}
                                            />
                                        </InputOTP>
                                    </FormControl>

                                    <FormMessage
                                        className={classNames?.error}
                                    />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="trustDevice"
                            render={({ field }) => (
                                <FormItem className="rounded-lg border border-border/60 bg-muted/30 p-3">
                                    <div className="flex items-start gap-3 text-left">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                disabled={isSubmitting}
                                                className={cn(classNames?.checkbox)}
                                            />
                                        </FormControl>

                                        <div className="grid gap-1">
                                            <FormLabel
                                                className={cn(
                                                    "cursor-pointer font-medium",
                                                    classNames?.label
                                                )}
                                            >
                                                {localization.TRUST_DEVICE}
                                            </FormLabel>

                                            <FormDescription className="text-xs leading-relaxed">
                                                {localization.TRUST_DEVICE_DESCRIPTION}
                                            </FormDescription>
                                        </div>
                                    </div>
                                </FormItem>
                            )}
                        />
                    </div>
                )}

                <div className="grid gap-4">
                    {method !== null && (
                        <div className="grid gap-4">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className={cn(
                                    "w-full",
                                    classNames?.button,
                                    classNames?.primaryButton
                                )}
                            >
                                {isSubmitting && (
                                    <Loader2 className="animate-spin" />
                                )}
                                {localization.TWO_FACTOR_ACTION}
                            </Button>

                            <div className="flex justify-center">
                                <Link
                                    className={cn(
                                        "text-muted-foreground text-sm hover:text-foreground hover:underline",
                                        classNames?.forgotPasswordLink
                                    )}
                                    href={`${basePath}/${viewPaths.RECOVER_ACCOUNT}`}
                                >
                                    {localization.FORGOT_AUTHENTICATOR}
                                </Link>
                            </div>
                        </div>
                    )}

                    {method === "otp" && twoFactor?.includes("otp") && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={sendOtp}
                            disabled={
                                cooldownSeconds > 0 ||
                                isSendingOtp ||
                                isSubmitting
                            }
                            className={cn(
                                classNames?.button,
                                classNames?.outlineButton
                            )}
                        >
                            {isSendingOtp ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                <SendIcon className={classNames?.icon} />
                            )}

                            {localization.RESEND_CODE}
                            {cooldownSeconds > 0 && ` (${cooldownSeconds})`}
                        </Button>
                    )}

                    {method !== "otp" && twoFactor?.includes("otp") && (
                        <Button
                            type="button"
                            variant="secondary"
                            className={cn(
                                classNames?.button,
                                classNames?.secondaryButton
                            )}
                            onClick={() => setMethod("otp")}
                            disabled={isSubmitting}
                        >
                            <SendIcon className={classNames?.icon} />
                            {localization.SEND_VERIFICATION_CODE}
                        </Button>
                    )}

                    {method !== "totp" && twoFactor?.includes("totp") && (
                        <Button
                            type="button"
                            variant="secondary"
                            className={cn(
                                classNames?.button,
                                classNames?.secondaryButton
                            )}
                            onClick={() => setMethod("totp")}
                            disabled={isSubmitting}
                        >
                            <QrCodeIcon className={classNames?.icon} />
                            {localization.CONTINUE_WITH_AUTHENTICATOR}
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    )
}
